import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ApiRequest from "../../../actions/ApiRequest";
import Toast from "components/common/Toast";
import Notiflix from "notiflix";

const PubgSettings = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [pubgData, setPubgData] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedData, setEditedData] = useState({});

  useEffect(() => {
    fetchPubgData();
  }, []);

  const fetchPubgData = async () => {
    setIsLoading(true);
    try {
      const response = await ApiRequest.post(
        "pubg_settings?operation=GET"
      );
      
      if (response?.data?.status === "success") {
        setPubgData(response.data.data || []);
      } else {
        Toast.fire({
          title: response?.data?.reason || "Failed to load PUBG data",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching PUBG data:", error);
      Toast.fire({
        title: "Something Went Wrong!",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingIndex(product.index);
    setEditedData({ ...product });
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditedData({});
  };

  const handleChange = (field, value) => {
    setEditedData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    Notiflix.Loading.standard("Saving...");
    try {
      const response = await ApiRequest.post(
        "pubg_settings?operation=UPDATE",
        editedData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response?.data?.status === "success") {
        Toast.fire({
          title: "PUBG product updated successfully!",
          icon: "success",
        });
        setEditingIndex(null);
        setEditedData({});
        fetchPubgData(); // Refresh data
      } else {
        Toast.fire({
          title: response?.data?.reason || "Failed to update",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error updating PUBG product:", error);
      Toast.fire({
        title: "Something Went Wrong!",
        icon: "error",
      });
    } finally {
      Notiflix.Loading.remove();
    }
  };

  const renderEditableCell = (product, field, label) => {
    const isEditing = editingIndex === product.index;
    const value = isEditing ? editedData[field] : product[field];

    if (isEditing) {
      return (
        <TextField
          size="small"
          value={value || ""}
          onChange={(e) => handleChange(field, e.target.value)}
          fullWidth
          variant="outlined"
        />
      );
    }

    return <Typography variant="body2">{value || "-"}</Typography>;
  };

  return (
    <Box
      sx={{
        margin: "20px auto",
        padding: "30px",
        boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.2)",
        borderRadius: "10px",
        color: "#000",
        width: "100%",
      }}
    >
      <Typography variant="h4" gutterBottom>
        PUBG UC Settings
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Manage PUBG UC products - Admin Only
      </Typography>

      {isLoading ? (
        <Box
          sx={{ display: "flex", justifyContent: "center", marginTop: "50px" }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ marginTop: 3, overflowX: "auto" }}>
          <Table sx={{ minWidth: 2000 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell><strong>Index</strong></TableCell>
                <TableCell><strong>Customer Price (₪)</strong></TableCell>
                <TableCell><strong>PhonePlay Cost (₪)</strong></TableCell>
                <TableCell><strong>Brand Name</strong></TableCell>
                <TableCell><strong>Brand Des</strong></TableCell>
                <TableCell><strong>Brand Image Path</strong></TableCell>
                <TableCell><strong>Brand Icon</strong></TableCell>
                <TableCell><strong>Brand Image Name</strong></TableCell>
                <TableCell><strong>Deno ID</strong></TableCell>
                <TableCell><strong>Deno Name</strong></TableCell>
                <TableCell><strong>Deno Des (EN)</strong></TableCell>
                <TableCell><strong>Deno Value</strong></TableCell>
                <TableCell><strong>Deno Currency</strong></TableCell>
                <TableCell><strong>Deno Barcode</strong></TableCell>
                <TableCell><strong>Deno Cost</strong></TableCell>
                <TableCell><strong>Deno Cont ID</strong></TableCell>
                <TableCell><strong>Deno Image Path</strong></TableCell>
                <TableCell><strong>Deno Image Name</strong></TableCell>
                <TableCell><strong>Deno Type</strong></TableCell>
                <TableCell><strong>Deno EAN</strong></TableCell>
                <TableCell><strong>Deno HE Des</strong></TableCell>
                <TableCell><strong>Deno AR Des</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pubgData.map((product) => (
                <TableRow key={product.index}>
                  <TableCell>{product.index}</TableCell>
                  <TableCell>{renderEditableCell(product, "customer_price", "Customer Price")}</TableCell>
                  <TableCell>{renderEditableCell(product, "phone_play_cost", "PhonePlay Cost")}</TableCell>
                  <TableCell>{renderEditableCell(product, "brand_name", "Brand Name")}</TableCell>
                  <TableCell>{renderEditableCell(product, "brand_des", "Brand Des")}</TableCell>
                  <TableCell>{renderEditableCell(product, "brand_imagepath", "Brand Image Path")}</TableCell>
                  <TableCell>{renderEditableCell(product, "brand_icon", "Brand Icon")}</TableCell>
                  <TableCell>{renderEditableCell(product, "brand_imagename", "Brand Image Name")}</TableCell>
                  <TableCell>{renderEditableCell(product, "deno_id", "Deno ID")}</TableCell>
                  <TableCell>{renderEditableCell(product, "deno_name", "Deno Name")}</TableCell>
                  <TableCell>{renderEditableCell(product, "deno_des", "Deno Des")}</TableCell>
                  <TableCell>{renderEditableCell(product, "deno_value", "Deno Value")}</TableCell>
                  <TableCell>{renderEditableCell(product, "deno_currency", "Deno Currency")}</TableCell>
                  <TableCell>{renderEditableCell(product, "deno_barcode", "Deno Barcode")}</TableCell>
                  <TableCell>{renderEditableCell(product, "deno_cost", "Deno Cost")}</TableCell>
                  <TableCell>{renderEditableCell(product, "deno_contid", "Deno Cont ID")}</TableCell>
                  <TableCell>{renderEditableCell(product, "deno_imagepath", "Deno Image Path")}</TableCell>
                  <TableCell>{renderEditableCell(product, "deno_imagename", "Deno Image Name")}</TableCell>
                  <TableCell>{renderEditableCell(product, "deno_type", "Deno Type")}</TableCell>
                  <TableCell>{renderEditableCell(product, "deno_ean", "Deno EAN")}</TableCell>
                  <TableCell>{renderEditableCell(product, "deno_he_des", "Deno HE Des")}</TableCell>
                  <TableCell>{renderEditableCell(product, "deno_ar_des", "Deno AR Des")}</TableCell>
                  <TableCell align="center">
                    {editingIndex === product.index ? (
                      <>
                        <IconButton
                          color="primary"
                          onClick={handleSave}
                          size="small"
                        >
                          <SaveIcon />
                        </IconButton>
                        <IconButton
                          color="secondary"
                          onClick={handleCancel}
                          size="small"
                        >
                          <CancelIcon />
                        </IconButton>
                      </>
                    ) : (
                      <IconButton
                        color="primary"
                        onClick={() => handleEdit(product)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default PubgSettings;

